import { Injectable, inject, signal } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { Firestore, doc, updateDoc, arrayUnion, arrayRemove } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface NotificationPayload {
  title: string;
  body: string;
  data?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly messaging = inject(Messaging);
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(Auth);

  readonly notificationPermission = signal<NotificationPermission>(
    globalThis.Notification?.permission ?? 'default'
  );
  readonly currentToken = signal<string | null>(null);
  readonly lastNotification = signal<NotificationPayload | null>(null);

  constructor() {
    this.listenToForegroundMessages();
  }

  requestPermission(): Observable<string | null> {
    if (!('Notification' in globalThis)) {
      console.warn('This browser does not support notifications');
      return of(null);
    }

    return from(Notification.requestPermission()).pipe(
      tap(permission => this.notificationPermission.set(permission)),
      switchMap(permission =>
        permission === 'granted' ? this.retrieveFcmToken() : of(null)
      ),
      catchError(error => {
        console.error('Error requesting notification permission:', error);
        return of(null);
      })
    );
  }

  private retrieveFcmToken(): Observable<string | null> {
    return from(navigator.serviceWorker.register('/firebase-messaging-sw.js')).pipe(
      switchMap(registration =>
        from(getToken(this.messaging, {
          vapidKey: environment.firebase.vapidKey,
          serviceWorkerRegistration: registration
        }))
      ),
      tap(token => {
        if (token) {
          this.currentToken.set(token);
          console.log('FCM Token:', token);
        }
      }),
      switchMap(token => token ? this.saveTokenToUser(token).pipe(map(() => token)) : of(null)),
      catchError(error => {
        console.error('Error retrieving FCM token:', error);
        return of(null);
      })
    );
  }

  private saveTokenToUser(token: string): Observable<void> {
    const user = this.auth.currentUser;
    if (!user) {
      console.warn('No authenticated user to save token');
      return of(undefined);
    }

    const userDocRef = doc(this.firestore, 'users', user.uid);
    return from(updateDoc(userDocRef, { tokens: arrayUnion(token) })).pipe(
      tap(() => console.log('Token saved to user document')),
      catchError(error => {
        console.error('Error saving token:', error);
        return of(undefined);
      })
    );
  }

  removeTokenFromUser(token: string): Observable<void> {
    const user = this.auth.currentUser;
    if (!user || !token) return of(undefined);

    const userDocRef = doc(this.firestore, 'users', user.uid);
    return from(updateDoc(userDocRef, { tokens: arrayRemove(token) }));
  }

  private listenToForegroundMessages(): void {
    onMessage(this.messaging, payload => {
      console.log('Foreground message:', payload);

      const notification: NotificationPayload = {
        title: payload.notification?.title ?? 'New Notification',
        body: payload.notification?.body ?? '',
        data: payload.data as Record<string, string>
      };

      this.lastNotification.set(notification);

      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.body,
          icon: '/icons/icon-192x192.png'
        });
      }
    });
  }
}